import { ReactNode, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../Breadcrumb';
import { safeReturnPath } from '../common/returnPath';
import type { BreadcrumbItem } from '../Breadcrumb';

export interface SettingsSection {
  key: string;
  label: string;
  badge?: string | number;
  danger?: boolean;
  /** Shows the machinery (raw transcripts, tool calls). Admins with internals
   *  revealed only — see contexts/InternalsContext. */
  internal?: boolean;
  // Optional inline icon shown to the left of the label. Pass any small
  // SVG (e.g. the sparkles icon for LLM-driven sections).
  icon?: ReactNode;
}

interface SettingsLayoutProps {
  title: string;
  scopeLabel: string; // "Subject" | "Section" | "Node"
  breadcrumb: BreadcrumbItem[];
  backTo: string; // route to return to the work surface
  backLabel: string; // e.g. "Back to Subject"
  sections: SettingsSection[];
  activeSection: string;
  onSectionChange: (key: string) => void;
  children: ReactNode;
  // Drop the default 4xl content cap so wide panels (e.g. the structure
  // planner's tree+chat split) can use the full available viewport width.
  fullWidth?: boolean;
}

function useIsNarrow(breakpoint = 768) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return narrow;
}

export default function SettingsLayout({
  title,
  scopeLabel,
  breadcrumb,
  backTo,
  backLabel,
  sections,
  activeSection,
  onSectionChange,
  children,
  fullWidth = false,
}: SettingsLayoutProps) {
  const isNarrow = useIsNarrow();
  const [searchParams] = useSearchParams();

  // ONE back button, and a deep-link's trail outranks the structural parent.
  //
  // `backTo` is where this screen sits in the tree — right when you walked down
  // to it, wrong when something threw you here from somewhere else (a version
  // link inside a build, a room's dossier). Rendering a ReturnTrail chip as well
  // would put two back buttons in a header bar built for one, so the trail
  // retargets the button that is already there.
  const returnPath = safeReturnPath(searchParams.get('from'));
  const exitTo = returnPath || backTo;
  const exitLabel = returnPath
    ? `Back to ${searchParams.get('from_label') || 'where you were'}`
    : backLabel;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 flex-shrink-0">
        {/* The shared trail, not a private copy of one. `mb-0` because this
            header owns its own spacing — Breadcrumb's default `mb-4 sm:mb-6`
            is sized for a page that starts with it, not for a compact header
            bar with a title underneath. */}
        <Breadcrumb items={breadcrumb} className="mb-1" />
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wider">
              {scopeLabel} settings
            </div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
              {title}
            </h1>
          </div>
          <Link
            to={exitTo}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap"
          >
            ← {exitLabel}
          </Link>
        </div>
      </div>

      {/* Body: sidebar nav (desktop) / top tabs (mobile) + content */}
      <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
        {isNarrow ? (
          // Mobile: scrollable horizontal tab bar
          <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <div className="flex gap-1 p-2 min-w-max">
              {sections.map((s) => (
                <button
                  key={s.key}
                  onClick={() => onSectionChange(s.key)}
                  className={`px-3 py-1.5 text-sm rounded whitespace-nowrap inline-flex items-center gap-1.5 ${
                    activeSection === s.key
                      ? s.danger
                        ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                        : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium'
                      : s.danger
                      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {s.icon && <span className="shrink-0">{s.icon}</span>}
                  <span>{s.label}</span>
                  {s.badge != null && (
                    <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">
                      {s.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : (
          // Desktop: left sidebar
          <nav className="w-56 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 py-4 px-2 overflow-y-auto">
            <ul className="space-y-1">
              {sections.map((s) => (
                <li key={s.key}>
                  <button
                    onClick={() => onSectionChange(s.key)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded text-left ${
                      activeSection === s.key
                        ? s.danger
                          ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-medium'
                          : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium'
                        : s.danger
                        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {s.icon && <span className="shrink-0">{s.icon}</span>}
                      <span className="truncate">{s.label}</span>
                    </span>
                    {s.badge != null && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{s.badge}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div
            className={`mx-auto px-4 sm:px-6 py-6 ${
              fullWidth ? 'max-w-none' : 'max-w-4xl'
            }`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
